import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import {
  Ban,
  CalendarPlus,
  CalendarRange,
  CircleCheck,
  Clock3,
  FlagTriangleRight,
  LockKeyhole,
  Pencil,
  Plus,
  Save,
  Trash2,
  UnlockKeyhole,
  UsersRound,
  X,
} from "lucide-react"
import { useEffect, useMemo, useState } from "react"
import type { Dispatch, FormEvent, SetStateAction } from "react"

import { StaffPagePending } from "@/components/loading-skeleton"
import { StaffStatusAction } from "@/components/staff-status-action"
import { StatusChip } from "@/components/status-chip"
import { Button } from "@/components/ui/button"
import {
  formatCourseDate,
  formatCurrency,
  formatMinutes,
  minutesToTimeInput,
  timeInputToMinutes,
} from "@/lib/date"
import { ACTIVE_BOOKING_STATUSES } from "@/lib/domain"
import {
  golfDeskQueryKeys,
  staffTeeSheetQueryOptions,
} from "@/lib/query-options"
import { cn } from "@/lib/utils"
import {
  createTeeTimeFn,
  deleteTeeTimeFn,
  fillTeeTimeDayFn,
  updateTeeTimeFn,
  updateTeeTimeStatusFn,
} from "@/server/functions"
import type { getStaffTeeSheetFn } from "@/server/functions"

type TeeSheetSearch = { course?: string; date?: string }

export const Route = createFileRoute("/staff/tee-sheet")({
  validateSearch: (search: Record<string, unknown>): TeeSheetSearch => ({
    course: typeof search.course === "string" ? search.course : undefined,
    date: typeof search.date === "string" ? search.date : undefined,
  }),
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) =>
    context.queryClient.ensureQueryData(staffTeeSheetQueryOptions(deps)),
  head: () => ({
    meta: [{ title: "Tee times · Groundline staff" }],
  }),
  pendingComponent: () => <StaffPagePending kind="sheet" />,
  component: TeeSheet,
})

type TeeSheetData = Awaited<ReturnType<typeof getStaffTeeSheetFn>>
type TeeSheetCourse = TeeSheetData["course"]
type TeeSheetTime = TeeSheetData["times"][number]

type TeeTimeDraft = {
  time: string
  holes: "9" | "18"
  capacity: string
  price: string
  status: "open" | "blocked"
  notes: string
}

function mutationError(reason?: string) {
  switch (reason) {
    case "ACTIVE_BOOKINGS":
      return "Move or cancel active bookings before blocking this tee time."
    case "CAPACITY_BELOW_BOOKINGS":
      return "Capacity cannot be lower than the active player count."
    case "CAPACITY_OUT_OF_RANGE":
      return "Capacity must stay within this course’s player limit."
    case "DATE_OUT_OF_RANGE":
      return "Choose a date within the available schedule window."
    case "DUPLICATE_TIME":
      return "Another tee time already uses that start time."
    case "HAS_BOOKINGS":
      return "This tee time has booking history. Block it instead of deleting it."
    case "TIME_OUT_OF_RANGE":
      return "Choose a time inside the course’s operating hours."
    case "NOT_FOUND":
      return "That tee time is no longer available. Refresh and try again."
    default:
      return "The tee time could not be updated. Please try again."
  }
}

function pesosFromCents(cents: number) {
  return (cents / 100).toFixed(cents % 100 === 0 ? 0 : 2)
}

function parseDraft(draft: TeeTimeDraft) {
  const startMinutes = timeInputToMinutes(draft.time)
  const capacity = Number(draft.capacity)
  const price = Number(draft.price)

  if (
    startMinutes === null ||
    !Number.isInteger(capacity) ||
    capacity < 1 ||
    !Number.isFinite(price) ||
    price < 0
  ) {
    return null
  }

  return {
    startMinutes,
    holes: draft.holes === "9" ? (9 as const) : (18 as const),
    capacity,
    basePriceCents: Math.round(price * 100),
    status: draft.status,
    notes: draft.notes,
  }
}

function suggestedStart(course: TeeSheetCourse, times: Array<TeeSheetTime>) {
  const occupied = new Set(times.map((time) => time.startMinutes))

  for (
    let start = course.firstTeeMinutes;
    start <= course.lastTeeMinutes;
    start += course.intervalMinutes
  ) {
    if (!occupied.has(start)) {
      return start
    }
  }

  for (
    let start = course.firstTeeMinutes;
    start <= course.lastTeeMinutes;
    start += 5
  ) {
    if (!occupied.has(start)) {
      return start
    }
  }

  return course.firstTeeMinutes
}

function TeeSheet() {
  const search = Route.useSearch()
  const { data } = useSuspenseQuery(staffTeeSheetQueryOptions(search))
  const navigate = Route.useNavigate()
  const queryClient = useQueryClient()
  const [pendingAction, setPendingAction] = useState<string | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [showAdd, setShowAdd] = useState(false)
  const [error, setError] = useState("")
  const [notice, setNotice] = useState("")
  const bookedPlayers = data.times.reduce(
    (sum, time) => sum + time.activePlayers,
    0
  )
  const missingDefaultTimes = useMemo(() => {
    const existing = new Set(data.times.map((time) => time.startMinutes))
    let missing = 0

    for (
      let start = data.course.firstTeeMinutes;
      start <= data.course.lastTeeMinutes;
      start += data.course.intervalMinutes
    ) {
      if (!existing.has(start)) {
        missing += 1
      }
    }

    return missing
  }, [data.course, data.times])

  useEffect(() => {
    setEditingId(null)
    setShowAdd(false)
    setError("")
    setNotice("")
  }, [data.course.id, data.date])

  async function refresh(message: string) {
    setEditingId(null)
    setShowAdd(false)
    await queryClient.invalidateQueries({
      queryKey: golfDeskQueryKeys.all,
    })
    setNotice(message)
  }

  async function fillDay() {
    setPendingAction("fill")
    setError("")
    setNotice("")

    try {
      const result = await fillTeeTimeDayFn({
        data: {
          courseSlug: data.course.slug,
          date: data.date,
        },
      })

      if (!result.ok) {
        setError(mutationError(result.reason))
        return
      }

      await refresh(
        result.created === 0
          ? "The default schedule is already in place."
          : `${result.created} missing tee ${
              result.created === 1 ? "time was" : "times were"
            } added.`
      )
    } catch {
      setError("The default schedule could not be added. Please try again.")
    } finally {
      setPendingAction(null)
    }
  }

  async function toggleTime(teeTimeId: string, status: "open" | "blocked") {
    setPendingAction(`status:${teeTimeId}`)
    setError("")
    setNotice("")

    try {
      const result = await updateTeeTimeStatusFn({
        data: {
          teeTimeId,
          status: status === "open" ? "blocked" : "open",
        },
      })

      if (!result.ok) {
        setError(mutationError(result.reason))
        return
      }

      await refresh(
        status === "open" ? "The tee time is blocked." : "The tee time is open."
      )
    } catch {
      setError("The tee time status could not be updated. Please try again.")
    } finally {
      setPendingAction(null)
    }
  }

  return (
    <>
      <header className="staff-page-head">
        <div>
          <span className="eyebrow">
            <CalendarRange className="size-4" />
            Inventory control
          </span>
          <h1 className="staff-page-title">Tee times.</h1>
          <p className="staff-page-copy">
            {data.course.name} · {formatCourseDate(data.date)} · {bookedPlayers}{" "}
            active player {bookedPlayers === 1 ? "spot" : "spots"}
          </p>
        </div>
        <div className="grid min-w-0 gap-3 sm:grid-cols-2 lg:min-w-[410px]">
          <div className="field">
            <label htmlFor="tee-sheet-course">Golf course</label>
            <select
              id="tee-sheet-course"
              className="control"
              value={data.course.slug}
              onChange={(event) =>
                navigate({
                  search: {
                    course: event.target.value,
                    date: data.date,
                  },
                })
              }
            >
              {data.courses.map((course) => (
                <option key={course.id} value={course.slug}>
                  {course.name}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="tee-sheet-date">Operating date</label>
            <input
              id="tee-sheet-date"
              className="control"
              type="date"
              min={data.today}
              max={data.lastDate}
              value={data.date}
              onChange={(event) =>
                navigate({
                  search: {
                    course: data.course.slug,
                    date: event.target.value,
                  },
                })
              }
            />
          </div>
        </div>
      </header>

      {error ? (
        <p className="action-error mb-5" role="alert">
          {error}
        </p>
      ) : null}
      {notice ? (
        <p className="action-success mb-5" role="status">
          <CircleCheck className="size-4" aria-hidden="true" />
          {notice}
        </p>
      ) : null}

      <section className="ruled-panel overflow-hidden">
        <div className="ruled-panel__head flex-wrap">
          <div>
            <span className="eyebrow">
              <CalendarPlus className="size-3.5" />
              Day setup
            </span>
            <p className="mt-1 text-xs leading-5 text-muted-foreground">
              Defaults run {formatMinutes(data.course.firstTeeMinutes)}–
              {formatMinutes(data.course.lastTeeMinutes)} every{" "}
              {data.course.intervalMinutes} minutes, with{" "}
              {data.course.maxPlayers} players per time.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={pendingAction !== null || missingDefaultTimes === 0}
              onClick={fillDay}
            >
              <CalendarPlus data-icon="inline-start" />
              {missingDefaultTimes === 0
                ? "Defaults applied"
                : `Fill ${missingDefaultTimes} missing`}
            </Button>
            <Button
              type="button"
              variant="accent"
              aria-expanded={showAdd}
              onClick={() => {
                setShowAdd((current) => !current)
                setEditingId(null)
              }}
            >
              {showAdd ? (
                <X data-icon="inline-start" />
              ) : (
                <Plus data-icon="inline-start" />
              )}
              {showAdd ? "Close form" : "Add tee time"}
            </Button>
          </div>
        </div>
        {showAdd ? (
          <AddTeeTimeForm
            key={`${data.course.id}-${data.date}`}
            course={data.course}
            date={data.date}
            initialStart={suggestedStart(data.course, data.times)}
            onCancel={() => setShowAdd(false)}
            onSaved={(message) => refresh(message)}
          />
        ) : (
          <div className="grid gap-4 px-4 py-4 text-xs text-muted-foreground sm:grid-cols-3 sm:px-5">
            <p>
              <strong className="block text-foreground">
                {data.times.length} scheduled
              </strong>
              Tee times on this course and day
            </p>
            <p>
              <strong className="block text-foreground">
                {formatCurrency(
                  data.course.greenFeeCents,
                  data.course.currency
                )}
              </strong>
              Default green fee per player
            </p>
            <p>
              <strong className="block text-foreground">
                {data.course.holes} holes
              </strong>
              New times inherit the course setup
            </p>
          </div>
        )}
      </section>

      <section className="ruled-panel mt-6 overflow-hidden">
        <div className="ruled-panel__head">
          <div>
            <span className="eyebrow">
              <FlagTriangleRight className="size-3.5" />
              Applied tee times
            </span>
            <p className="mt-1 text-xs text-muted-foreground">
              Edit operating details here; bookings remain attached to their
              time.
            </p>
          </div>
          <div className="hidden items-center gap-3 text-[0.68rem] font-semibold text-muted-foreground sm:flex">
            <span className="inline-flex items-center gap-1.5">
              <span className="size-2 rounded-full bg-primary" />
              Booked
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="size-2 rounded-[2px] bg-muted-foreground" />
              Open
            </span>
          </div>
        </div>

        {data.times.length === 0 ? (
          <div className="empty-state">
            <div>
              <Clock3 className="size-6" aria-hidden="true" />
              <strong>No tee times set for this day</strong>
              <p className="mt-2">
                Fill the course defaults or add an individual time above.
              </p>
            </div>
          </div>
        ) : (
          data.times.map((time) => {
            const activeBookings = time.bookings.filter((booking) =>
              ACTIVE_BOOKING_STATUSES.includes(
                booking.status as (typeof ACTIVE_BOOKING_STATUSES)[number]
              )
            )
            const hasActiveBooking = activeBookings.length > 0
            const isStatusPending = pendingAction === `status:${time.id}`
            const isEditing = editingId === time.id

            return (
              <article
                key={time.id}
                className={cn(
                  "rule-row",
                  time.status === "blocked" && "tee-time-row--blocked",
                  time.spotsLeft === 0 &&
                    time.status === "open" &&
                    "tee-time-row--full"
                )}
              >
                <div className="grid gap-3 px-4 py-3 sm:px-5 lg:grid-cols-[104px_98px_minmax(0,1fr)_auto] lg:items-center">
                  <div className="tee-time-stem relative border-l-2 pl-5">
                    <span
                      className={cn(
                        "border-paper absolute top-1/2 left-[-6px] size-2.5 -translate-y-1/2 rounded-[2px] border-2",
                        time.status === "blocked"
                          ? "tee-time-fix--blocked"
                          : hasActiveBooking
                            ? "tee-time-fix--booked"
                            : "tee-time-fix--open"
                      )}
                      aria-hidden="true"
                    />
                    <strong className="font-display text-[1.55rem] leading-none font-semibold">
                      {formatMinutes(time.startMinutes)}
                    </strong>
                  </div>
                  <div>
                    {time.status === "blocked" ? (
                      <span className="status-chip status-chip--cancelled">
                        <Ban className="size-3" />
                        Blocked
                      </span>
                    ) : time.spotsLeft === 0 ? (
                      <span className="status-chip status-chip--checked_in">
                        <CircleCheck className="size-3" />
                        Full
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
                        <UsersRound className="size-3.5" />
                        {time.spotsLeft} open
                      </span>
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className="mb-2 flex flex-wrap gap-x-3 gap-y-1 text-[0.7rem] font-semibold text-muted-foreground">
                      <span>{time.holes} holes</span>
                      <span>
                        {formatCurrency(
                          time.basePriceCents,
                          data.course.currency
                        )}{" "}
                        per player
                      </span>
                      <span>{time.capacity} max</span>
                      {time.notes ? <span>{time.notes}</span> : null}
                    </div>
                    {time.bookings.length === 0 ? (
                      <p className="text-xs text-muted-foreground">
                        No group assigned
                      </p>
                    ) : (
                      <div className="flex flex-wrap items-center gap-2">
                        {time.bookings.map((booking) => (
                          <span
                            key={booking.id}
                            className="bg-paper inline-flex min-h-11 items-center gap-2 rounded-[8px] border border-border px-2.5 text-xs"
                          >
                            <strong>{booking.leadPlayerName}</strong>
                            <span className="text-muted-foreground">
                              ×{booking.playerCount}
                            </span>
                            <StatusChip
                              status={booking.status}
                              className="hidden xl:inline-flex"
                            />
                            <StaffStatusAction
                              bookingId={booking.id}
                              status={booking.status}
                              compact
                            />
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1 justify-self-start lg:justify-self-end">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      aria-label={`Edit ${formatMinutes(time.startMinutes)}`}
                      title="Edit tee time"
                      aria-expanded={isEditing}
                      onClick={() => {
                        setEditingId(isEditing ? null : time.id)
                        setShowAdd(false)
                      }}
                    >
                      {isEditing ? <X /> : <Pencil />}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      aria-label={
                        time.status === "open"
                          ? `Block ${formatMinutes(time.startMinutes)}`
                          : `Open ${formatMinutes(time.startMinutes)}`
                      }
                      title={
                        hasActiveBooking
                          ? "Move or cancel active bookings first"
                          : time.status === "open"
                            ? "Block tee time"
                            : "Reopen tee time"
                      }
                      disabled={isStatusPending || hasActiveBooking}
                      onClick={() => toggleTime(time.id, time.status)}
                    >
                      {time.status === "open" ? (
                        <LockKeyhole />
                      ) : (
                        <UnlockKeyhole />
                      )}
                    </Button>
                  </div>
                </div>
                {isEditing ? (
                  <EditTeeTimeForm
                    key={time.id}
                    course={data.course}
                    time={time}
                    onCancel={() => setEditingId(null)}
                    onSaved={(message) => refresh(message)}
                  />
                ) : null}
              </article>
            )
          })
        )}
      </section>
    </>
  )
}

function TeeTimeFields({
  draft,
  setDraft,
  course,
  activePlayers = 0,
}: {
  draft: TeeTimeDraft
  setDraft: Dispatch<SetStateAction<TeeTimeDraft>>
  course: TeeSheetCourse
  activePlayers?: number
}) {
  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-6">
      <div className="field">
        <label htmlFor="managed-tee-time">Start time</label>
        <input
          id="managed-tee-time"
          className="control"
          type="time"
          min={minutesToTimeInput(course.firstTeeMinutes)}
          max={minutesToTimeInput(course.lastTeeMinutes)}
          value={draft.time}
          required
          onChange={(event) =>
            setDraft((current) => ({ ...current, time: event.target.value }))
          }
        />
      </div>
      <div className="field">
        <label htmlFor="managed-tee-capacity">Player capacity</label>
        <input
          id="managed-tee-capacity"
          className="control"
          type="number"
          min={Math.max(1, activePlayers)}
          max={course.maxPlayers}
          step="1"
          value={draft.capacity}
          required
          onChange={(event) =>
            setDraft((current) => ({
              ...current,
              capacity: event.target.value,
            }))
          }
        />
      </div>
      <div className="field">
        <label htmlFor="managed-tee-holes">Round length</label>
        <select
          id="managed-tee-holes"
          className="control"
          value={draft.holes}
          onChange={(event) =>
            setDraft((current) => ({
              ...current,
              holes: event.target.value as "9" | "18",
            }))
          }
        >
          <option value="18">18 holes</option>
          <option value="9">9 holes</option>
        </select>
      </div>
      <div className="field">
        <label htmlFor="managed-tee-price">Green fee ({course.currency})</label>
        <input
          id="managed-tee-price"
          className="control"
          type="number"
          min="0"
          step="1"
          value={draft.price}
          required
          onChange={(event) =>
            setDraft((current) => ({ ...current, price: event.target.value }))
          }
        />
      </div>
      <div className="field">
        <label htmlFor="managed-tee-status">Availability</label>
        <select
          id="managed-tee-status"
          className="control"
          value={draft.status}
          onChange={(event) =>
            setDraft((current) => ({
              ...current,
              status: event.target.value as "open" | "blocked",
            }))
          }
        >
          <option value="open">Open</option>
          <option value="blocked" disabled={activePlayers > 0}>
            Blocked
          </option>
        </select>
      </div>
      <div className="field">
        <label htmlFor="managed-tee-notes">Staff note</label>
        <input
          id="managed-tee-notes"
          className="control"
          type="text"
          maxLength={240}
          placeholder="Optional"
          value={draft.notes}
          onChange={(event) =>
            setDraft((current) => ({ ...current, notes: event.target.value }))
          }
        />
      </div>
    </div>
  )
}

function AddTeeTimeForm({
  course,
  date,
  initialStart,
  onCancel,
  onSaved,
}: {
  course: TeeSheetCourse
  date: string
  initialStart: number
  onCancel: () => void
  onSaved: (message: string) => Promise<void>
}) {
  const [draft, setDraft] = useState<TeeTimeDraft>({
    time: minutesToTimeInput(initialStart),
    holes: course.holes === 9 ? "9" : "18",
    capacity: String(course.maxPlayers),
    price: pesosFromCents(course.greenFeeCents),
    status: "open",
    notes: "",
  })
  const [pending, setPending] = useState(false)
  const [error, setError] = useState("")

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const values = parseDraft(draft)

    if (!values) {
      setError("Enter a valid time, capacity, and green fee.")
      return
    }

    setPending(true)
    setError("")

    try {
      const result = await createTeeTimeFn({
        data: {
          courseSlug: course.slug,
          date,
          ...values,
        },
      })

      if (!result.ok) {
        setError(mutationError(result.reason))
        return
      }

      await onSaved(
        `The ${formatMinutes(values.startMinutes)} tee time was added.`
      )
    } catch {
      setError("The tee time could not be added. Please try again.")
    } finally {
      setPending(false)
    }
  }

  return (
    <form
      className="border-t border-border bg-[#f7faf9] p-4 sm:p-5"
      onSubmit={submit}
    >
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <strong className="text-sm">Add one tee time</strong>
          <p className="mt-1 text-xs text-muted-foreground">
            Operating hours are {formatMinutes(course.firstTeeMinutes)}–
            {formatMinutes(course.lastTeeMinutes)}.
          </p>
        </div>
      </div>
      <TeeTimeFields draft={draft} setDraft={setDraft} course={course} />
      {error ? (
        <p className="action-error mt-4" role="alert">
          {error}
        </p>
      ) : null}
      <div className="mt-4 flex flex-wrap justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          disabled={pending}
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button type="submit" variant="accent" disabled={pending}>
          <Plus data-icon="inline-start" />
          {pending ? "Adding…" : "Add tee time"}
        </Button>
      </div>
    </form>
  )
}

function EditTeeTimeForm({
  course,
  time,
  onCancel,
  onSaved,
}: {
  course: TeeSheetCourse
  time: TeeSheetTime
  onCancel: () => void
  onSaved: (message: string) => Promise<void>
}) {
  const [draft, setDraft] = useState<TeeTimeDraft>({
    time: minutesToTimeInput(time.startMinutes),
    holes: time.holes === 9 ? "9" : "18",
    capacity: String(time.capacity),
    price: pesosFromCents(time.basePriceCents),
    status: time.status,
    notes: time.notes ?? "",
  })
  const [pending, setPending] = useState<"save" | "delete" | null>(null)
  const [error, setError] = useState("")

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const values = parseDraft(draft)

    if (!values) {
      setError("Enter a valid time, capacity, and green fee.")
      return
    }

    setPending("save")
    setError("")

    try {
      const result = await updateTeeTimeFn({
        data: {
          teeTimeId: time.id,
          ...values,
        },
      })

      if (!result.ok) {
        setError(mutationError(result.reason))
        return
      }

      await onSaved(
        `The ${formatMinutes(values.startMinutes)} tee time was updated.`
      )
    } catch {
      setError("The tee time could not be saved. Please try again.")
    } finally {
      setPending(null)
    }
  }

  async function remove() {
    const confirmed = window.confirm(
      `Delete the ${formatMinutes(time.startMinutes)} tee time from ${formatCourseDate(
        time.date
      )}?`
    )

    if (!confirmed) {
      return
    }

    setPending("delete")
    setError("")

    try {
      const result = await deleteTeeTimeFn({
        data: { teeTimeId: time.id },
      })

      if (!result.ok) {
        setError(mutationError(result.reason))
        return
      }

      await onSaved(
        `The ${formatMinutes(time.startMinutes)} tee time was removed.`
      )
    } catch {
      setError("The tee time could not be removed. Please try again.")
    } finally {
      setPending(null)
    }
  }

  return (
    <form
      className="border-t border-border bg-[#f7faf9] p-4 sm:p-5"
      onSubmit={submit}
    >
      <div className="mb-4">
        <strong className="text-sm">
          Edit {formatMinutes(time.startMinutes)}
        </strong>
        <p className="mt-1 text-xs text-muted-foreground">
          {time.canDelete
            ? "This unused time can be edited or removed."
            : "Booking history is retained; block this time if it should no longer be sold."}
        </p>
      </div>
      <TeeTimeFields
        draft={draft}
        setDraft={setDraft}
        course={course}
        activePlayers={time.activePlayers}
      />
      {error ? (
        <p className="action-error mt-4" role="alert">
          {error}
        </p>
      ) : null}
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <Button
          type="button"
          variant="destructive"
          disabled={pending !== null || !time.canDelete}
          title={
            time.canDelete
              ? "Delete tee time"
              : "Tee times with booking history cannot be deleted"
          }
          onClick={remove}
        >
          <Trash2 data-icon="inline-start" />
          Delete
        </Button>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="ghost"
            disabled={pending !== null}
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={pending !== null}>
            <Save data-icon="inline-start" />
            {pending === "save" ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </div>
    </form>
  )
}
